import { useEffect, useState } from "react";
import { useExpenses } from "./provider";
import ActiveCell from "../Companies/CompanyTable/ActiveCell";
import TabsList from "../../ui/TabsList";
import CustomTable from "../../ui/CustomTable";
import { useAuth } from "../../services/auth";
import ModalBox from "../../ui/ModalBox";
import { Alert, Snackbar } from "@mui/material";
import ErrorsList from "../../ui/ErrorsList";
import { useNavigate } from "react-router-dom";
import RestrictedCell from "./Render/RestrictedCell";

const ListExpenses = () => {
  const navigate = useNavigate();
  const { role } = useAuth();

  const [selectedCols, setSelectedCols] = useState("Expenses");
  const [openSuccessUpdate, setOpenSuccessUpdate] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingIds, setLoadingIds] = useState(new Set()); 
  const {
    state: { list, length },
    fetchList,
    dispatch,
    remove: removeExpense,
  } = useExpenses();

  const tabs = [
    {
      title: "Expenses",
      num: length,
    },
  ];

  const col = [
    {
      label: "Title",
      name: "title",
      headTextLeft: true,
      textLeft: true,
    },
    {
      label: "Account Title",
      name: "accoutTitle",
    },
    {
      label: "Expense Status",
      name: "isActive",
      customRender: ActiveCell,
    },
    {
      label: "Restricrt Status",
      name: "restricted",
      customRender: RestrictedCell,
    },
  ];

  useEffect(() => {
    fetchList().catch((error) => {
      setError({
        title: "Failed to load expenses",
        details: error?.message || "Please try again later",
      });
    });
  }, []);

  const toggleSuccessUpdate = () => {
    setOpenSuccessUpdate(!openSuccessUpdate);
  };

  const handleDelete = async (id, rowData) => {
    // Add the ID to loading set
    setLoadingIds((prev) => new Set(prev).add(id));
    
    // Clear any previous errors
    setError(null);
    
    try {
      await removeExpense(id);
      
      // Show success message
      setSuccessMessage(`Expense "${rowData?.title || 'item'}" deleted successfully`);
      
      // Refresh the list
      await fetchList();
      
    } catch (error) {
      // Handle different types of errors
      const errorDetails = {
        title: "Failed to delete expense",
        details: []
      };

      // Check for API response errors
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        errorDetails.details.push(`Status: ${error.response.status}`);
        
        if (error.response.data) {
          if (error.response.data.message) {
            errorDetails.details.push(`Message: ${error.response.data.message}`);
          }
          if (error.response.data.errors) {
            Object.entries(error.response.data.errors).forEach(([key, value]) => {
              errorDetails.details.push(`${key}: ${value}`);
            });
          }
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorDetails.details.push("No response received from server. Please check your connection.");
      } else if (error.message) {
        // Something happened in setting up the request that triggered an Error
        errorDetails.details.push(`Error: ${error.message}`);
      } else if (error.errors?.message) {
        // Your existing error format
        errorDetails.details.push(`Error: ${error.errors.message}`);
      } else if (typeof error === 'string') {
        errorDetails.details.push(error);
      } else {
        errorDetails.details.push("An unknown error occurred. Please try again.");
      }

      setError(errorDetails);
      
      // Log for debugging
      console.error("Delete expense error:", error);
      
      // Re-throw the error so the parent component/table can handle it if needed
      throw error;
    } finally {
      // Remove the ID from loading set
      setLoadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleEdit = (id, row) => {
    dispatch({ type: "set_selectedExpense", payload: row });
    navigate("edit/" + id);
  };

  const handleCloseError = () => {
    setError(null);
  };

  const handleCloseSuccess = () => {
    setSuccessMessage("");
  };

  return (
    <>
      {/* Error Alert - More prominent placement */}
      {error && (
        <Alert 
          severity="error" 
          onClose={handleCloseError}
          sx={{ mb: 2 }}
        >
          <ErrorsList error={error} />
        </Alert>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />

      <TabsList
        tabs={tabs}
        setSelectedCols={setSelectedCols}
        selectedCols={selectedCols}
      />
      
      <CustomTable
        empty={list?.length === 0}
        cols={col}
        data={list || []}
        filters={{
          add: () => {
            navigate("add");
          },
        }}
        actions={
          role === "Admin" && {
            delete: (id, row) => handleDelete(id, row),
            edit: handleEdit,
            // You can pass loading state to disable buttons during delete
            getRowLoadingState: (row) => loadingIds.has(row.id),
          }
        }
        isPagination={false}
      />
      
      <ModalBox
        open={openSuccessUpdate}
        operation="edit"
        handleClose={toggleSuccessUpdate}
        text="The update operation has been completed successfully."
        name={`${selectedCols}`}
      />
    </>
  );
};

export default ListExpenses;